import dataFolwStore from "@/store/endSideFlow.store";
import WifiHandler from "@/utils/wifiHandler";
import { ScreenShareApis, ScreenShareRecorder, WifiInfo } from "@core/ability";
import { useEffect, useReducer } from "react";
import IcConnecting from "@/assets/images/ic_connecting.png";
import IcConnect from "@/assets/svg/ic_connect.svg?react";
import IcCloseCircle from "@/assets/svg/ic_close_circle.svg?react";
import "./index.css";
import fridayReporter from "@/utils/friday";

enum ConnectorState {
  Disabled = "disabled",
  Ready = "ready",
  Connecting = "connecting",
  Error = "error",
}

interface ConnectState {
  status: ConnectorState;
  code: string;
  guessCode: string;
  errorMsg: string;
}

const initialState: ConnectState = {
  status: ConnectorState.Disabled,
  code: "",
  guessCode: "",
  errorMsg: "",
};

const connectReducer = (state: ConnectState, action: Partial<ConnectState>) => {
  return { ...state, ...action }
};

interface IProp {
  screenShareRecorder: ScreenShareRecorder;
}

let deviceList: WifiInfo[] = [];

let scanInterval: NodeJS.Timeout | number | undefined = undefined;

export default function ConnectView(prop: IProp) {

  const [connectState, dispatch] = useReducer(connectReducer, initialState);
  const { status, code, guessCode, errorMsg } = connectState;

  useEffect(() => {
    scanDevices();
    initTimer();
    return () => {
      clearInterval(scanInterval);
    }
  }, []);

  // 每隔5秒来一次扫描
  const initTimer = () => {
    scanInterval = setInterval(scanDevices, 10000);
  }

  const scanDevices = async () => {
    console.log('>>> scanDevices');
    const res = await ScreenShareApis.scanDevices();
    if (!res.data.length) return;

    const wifiInfos: WifiInfo[] = Object.values(res.data);
    const wifiList = WifiHandler.filterWifiList(
      WifiHandler.sortWifiListWithRssi(wifiInfos)
    );
    deviceList = wifiList;
  };

  const handleInputCode = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (status === ConnectorState.Connecting) return;
    const code = e.target.value.toUpperCase().replace(/[^a-zA-Z0-9]/g, "");
    if (code.length === 0) {
      dispatch({ code: "", guessCode: "" });
      return;
    }
    console.log(deviceList.length);
    const findCode = deviceList.find((device) => device.pinCode?.startsWith(code));
    console.log('>>> findCode', findCode);
    const guessCode = findCode?.pinCode || "";
    const codeStatus =
      code.length === 6 || code.length === 8
        ? ConnectorState.Ready
        : ConnectorState.Disabled;
    dispatch({
      status: codeStatus, code, guessCode, errorMsg: " ",
    });
  };

  const handleEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== "Enter") return;
    if (status === ConnectorState.Connecting) return;
    const currentCode = e.currentTarget.value.toUpperCase();
    const guessCode = deviceList.find((device) =>
      device.pinCode?.startsWith(currentCode)
    )?.pinCode;
    if (!guessCode) {
      if (status === ConnectorState.Disabled) {
        console.log('输入格式不对，返回');
        return;
      }
      e.currentTarget.blur();
      handleConnect(currentCode, true);
    } else {
      e.currentTarget.blur();
      dispatch({
        code: guessCode,
        guessCode,
        status: ConnectorState.Ready,
        errorMsg: "",
      });
      handleConnect(guessCode, true);
    }
  };

  const handleConnect = async (
    code: string,
    skipStatusCheck: boolean = false
  ) => {
    if (status !== ConnectorState.Ready && !skipStatusCheck) return;
    const device = deviceList.find((device) => device.pinCode === code);

    try {
      dispatch({ status: ConnectorState.Connecting, errorMsg: "" });
      await prop.screenShareRecorder.connectDevice(device, code);
      const res = await ScreenShareApis.getRemoteAddresss();
      console.log('getRemoteAddresss res = ', res.data.ip);

      dataFolwStore.getState().setIp(res.data.ip);
      dataFolwStore.getState().changeSection(2);
      fridayReporter.pushData({
        eventCode: "HomePage_GetFiles_LinkStatus",
        payload: {
          link_status: "success",
        },
      });
    } catch (error) {
      console.log("connect error", error);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errMsg = (error as any)?.message || "连接失败"
      dispatch({ status: ConnectorState.Error, errorMsg: errMsg });
      fridayReporter.pushData({
        eventCode: "HomePage_GetFiles_LinkStatus",
        payload: {
          link_status: "fail",
        },
      });
    }
  };

  const handleClear = () => {
    dispatch({
      code: "",
      guessCode: "",
      status: ConnectorState.Disabled,
      errorMsg: "",
    });
  };

  const handleClickConnect = () => {
    handleConnect(code);
  };

  return (
    <div className="flex flex-col flex-1 items-center">
      {/* <span className="text-size-5 text-mx-secondary mt-9 mb-1.5 font-600">获取大屏AI会记文件</span> */}
      {/* <span className="text-size-3 text-mx-quaternary mt-9">分享码在会议总结页的左下方</span> */}
      <div className="mt-18 w-70 h-11.25 flex items-center rounded-full border border-solid border-mx-quaternary" >
        <div className="relative flex-1 h-full">
          {!code && !guessCode && (
            <input
              type="text"
              readOnly
              placeholder="请输入6/8位分享码"
              className="code-input placeholder-#CED2D9 focus:outline-none"
            />
          )}
          {guessCode && (
            <input
              type="text"
              readOnly
              value={guessCode}
              className="code-input tracking-1.5 c-#CED2D9 focus:outline-none"
            />
          )}
          <input
            type="text"
            className="code-input tracking-1.5 c-mx-primary focus:outline-none placeholder-#ccccc"
            spellCheck={false}
            maxLength={8}
            value={code}
            onChange={handleInputCode}
            onKeyUp={handleEnter}
            tabIndex={-1}
            autoFocus
          />
        </div>
        {status === ConnectorState.Error && (
          <IcCloseCircle
            className="w-3 h-3 m-r-3 cursor-pointer"
            onClick={handleClear}
          />
        )}
        <div
          className={`w-6 h-6 min-w-6 m-r-1 rounded-full ${status === ConnectorState.Disabled
            ? "bg-mx-primary op-60"
            : status === ConnectorState.Ready ||
              status === ConnectorState.Connecting
              ? "bg-#4981F2 cursor-pointer"
              : status === ConnectorState.Error
                ? "bg-#FF584C"
                : ""
            }`}
          onClick={handleClickConnect}
        >
          {status === ConnectorState.Connecting ? (
            // 设计表示切不出来svg，只能用图片代替
            <img className="w-6 h-6 animate-spin" src={IcConnecting} />
          ) : (
            <IcConnect className="w-6 h-6" />
          )}
        </div>
      </div>
      {guessCode &&
        status !== ConnectorState.Connecting &&
        status !== ConnectorState.Error && (
          <div className="m-t-2 text-center text-#CED2D9 text-3 line-height-4.25">
            Enter快速补全分享码
          </div>
        )}
      {status === ConnectorState.Error && (
        <div className="m-t-2 text-center text-#FF584C text-3 line-height-4.25">
          {errorMsg}
        </div>
      )}
    </div>
  );
}